import {
  createShapeId,
  Mat,
  StateNode,
  TLDrawShapeSegment,
  TLPointerEventInfo,
  TLShapeId,
  TLShapePartial,
  toFixed,
  Vec,
} from "tldraw"

import { CustomHighlightShape } from "../CustomHighlightShape"
import { last } from "lodash"
import { STROKE_SIZES } from "../shared/default-shape-constants"
import { LDPage } from "../../shared/LDPage"
import { MarkerShape } from "../../shared/MarkerShape"
import {
  CustomDrawShape,
  customDrawShapeProps,
} from "../../shared/CustomDrawShape"

import { getStrokePoints } from "../shapes/shared/freehand/getStrokePoints"
import { getFreehandOptions } from "../shapes/draw/getPath"
import { toProxyShape } from "../shapes/MarkerShapeUtil"
import { svgInk, svgInk2 } from "../shapes/shared/freehand/svgInk"
// / @ts-ignore
import * as PolygonClipping from "polygon-clipping"
// @ts-ignore
import PolyBool from "polybooljs"
import path from "path"
import { Color, Expr } from "../../shared/Expr"
import { LDInstanceMeta } from "../../shared/LDInstance"
import { StrokeOptions } from "../shapes/shared/freehand/types"
import { getPointsFromSegments } from "../shapes/CustomDrawShapeUtil"

export type Point = [number, number]
export type Path = Point[]
export type Shape = Path[]

export function simplifyPolygon(shape: Point[]): Shape {
  // Convert {x, y} to [x, y]
  const ring: [number, number][] = shape
  // Ensure ring is closed
  if (
    ring.length < 3 ||
    ring[0][0] !== ring[ring.length - 1][0] ||
    ring[0][1] !== ring[ring.length - 1][1]
  ) {
    ring.push([ring[0][0], ring[0][1]])
  }

  try {
    //@ts-ignore
    const result = PolygonClipping.default.union([[ring]])

    return result.flatMap((polygons: any[]) =>
      polygons.map((segment) =>
        segment.map((point: Point[]) => {
          return [point[0], point[1]]
        }),
      ),
    )
  } catch (e) {
    console.warn("Simplification failed:", e)
    return [shape] // fallback: return original shape
  }
}

export function unionFillShapes(shapes: Shape[]): Point[][] {
  if (shapes.length === 0) return []

  const closedPolygons: Shape[] = shapes.map((ring) => {
    const isClosed =
      ring[0][0] === ring[ring.length - 1][0] &&
      ring[0][1] === ring[ring.length - 1][1]
    const closedRing = isClosed ? ring : [...ring, ring[0]]
    return closedRing
  })
  console.log("closedPolygons", closedPolygons)

  // Ensure the top-level structure is a MultiPolygon: Polygon[][]
  // @ts-ignore
  const result = PolygonClipping.default.union(shapes)
  console.log("result", result)

  // Flatten polygons (returning all rings, including holes)
  return result.flat()
}

export function arcToPoints(
  from: Point,
  rx: number,
  ry: number,
  xAxisRotation: number,
  largeArcFlag: number,
  sweepFlag: number,
  to: Point,
  segments = 10, // controls smoothness
): Point[] {
  const rad = (xAxisRotation * Math.PI) / 180

  // Translate the coordinate system to the midpoint between from and to
  const dx = (from[0] - to[0]) / 2
  const dy = (from[1] - to[1]) / 2

  // Rotate by -xAxisRotation
  const x1p = Math.cos(rad) * dx + Math.sin(rad) * dy
  const y1p = -Math.sin(rad) * dx + Math.cos(rad) * dy

  // Compute the center
  let rxSq = rx * rx
  let rySq = ry * ry
  let x1pSq = x1p * x1p
  let y1pSq = y1p * y1p

  let factor = Math.sqrt(
    Math.max(
      0,
      (rxSq * rySq - rxSq * y1pSq - rySq * x1pSq) /
        (rxSq * y1pSq + rySq * x1pSq),
    ),
  )
  if (largeArcFlag === sweepFlag) factor = -factor

  const cxp = factor * ((rx * y1p) / ry)
  const cyp = factor * (-(ry * x1p) / rx)

  // Rotate back to get (cx, cy)
  const cx = Math.cos(rad) * cxp - Math.sin(rad) * cyp + (from[0] + to[0]) / 2
  const cy = Math.sin(rad) * cxp + Math.cos(rad) * cyp + (from[1] + to[1]) / 2

  // Calculate the start and end angles
  const angleStart = Math.atan2((y1p - cyp) / ry, (x1p - cxp) / rx)
  const x2p = -x1p
  const y2p = -y1p
  const angleEnd = Math.atan2((y2p - cyp) / ry, (x2p - cxp) / rx)

  let deltaAngle = angleEnd - angleStart
  if (sweepFlag === 0 && deltaAngle > 0) {
    deltaAngle -= 2 * Math.PI
  } else if (sweepFlag === 1 && deltaAngle < 0) {
    deltaAngle += 2 * Math.PI
  }

  const points: Point[] = []
  for (let i = 1; i <= segments; i++) {
    const angle = angleStart + (deltaAngle * i) / segments
    const x =
      Math.cos(rad) * rx * Math.cos(angle) -
      Math.sin(rad) * ry * Math.sin(angle) +
      cx
    const y =
      Math.sin(rad) * rx * Math.cos(angle) +
      Math.cos(rad) * ry * Math.sin(angle) +
      cy
    points.push([x, y])
  }

  return points
}

export const parseSVGPath1 = (d: string): Path[] => {
  const commands = d.match(/[a-df-z][^a-df-z]*/gi)
  if (!commands) return []

  let currentPoint: Point = [0, 0]
  let currentPath: Path = []
  let paths: Path[] = []

  for (const command of commands) {
    const type = command[0]
    const values = command
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .map(Number)
    let i = 0

    switch (type) {
      case "M": // Move to (absolute)
        if (currentPath.length) paths.push(currentPath)
        currentPath = []
        currentPoint = [values[i++], values[i++]]
        currentPath.push([...currentPoint])
        break

      case "m": // Move to (relative)
        if (currentPath.length) paths.push(currentPath)
        currentPath = []
        currentPoint = [
          currentPoint[0] + values[i++],
          currentPoint[1] + values[i++],
        ]
        currentPath.push([...currentPoint])
        break

      case "L": // Line to (absolute)
        while (i < values.length) {
          currentPoint = [values[i++], values[i++]]
          currentPath.push([...currentPoint])
        }
        break

      case "l": // Line to (relative)
        while (i < values.length) {
          currentPoint = [
            currentPoint[0] + values[i++],
            currentPoint[1] + values[i++],
          ]
          currentPath.push([...currentPoint])
        }
        break

      case "H": // Horizontal line (absolute)
        while (i < values.length) {
          currentPoint = [values[i++], currentPoint[1]]
          currentPath.push([...currentPoint])
        }
        break

      case "h": // Horizontal line (relative)
        while (i < values.length) {
          currentPoint = [currentPoint[0] + values[i++], currentPoint[1]]
          currentPath.push([...currentPoint])
        }
        break

      case "V": // Vertical line (absolute)
        while (i < values.length) {
          currentPoint = [currentPoint[0], values[i++]]
          currentPath.push([...currentPoint])
        }
        break

      case "v": // Vertical line (relative)
        while (i < values.length) {
          currentPoint = [currentPoint[0], currentPoint[1] + values[i++]]
          currentPath.push([...currentPoint])
        }
        break

      case "C": // Cubic Bézier Curve (absolute)
        while (i + 5 < values.length) {
          const cp1: Point = [values[i++], values[i++]]
          const cp2: Point = [values[i++], values[i++]]
          currentPoint = [values[i++], values[i++]]
          currentPath.push([...currentPoint]) // Store only endpoint
        }
        break

      case "c": // Cubic Bézier Curve (relative)
        while (i + 5 < values.length) {
          const cp1: Point = [
            currentPoint[0] + values[i++],
            currentPoint[1] + values[i++],
          ]
          const cp2: Point = [
            currentPoint[0] + values[i++],
            currentPoint[1] + values[i++],
          ]
          currentPoint = [
            currentPoint[0] + values[i++],
            currentPoint[1] + values[i++],
          ]
          currentPath.push([...currentPoint]) // Store only endpoint
        }
        break

      case "T": // Smooth quadratic Bézier curve (absolute)
        while (i + 1 < values.length) {
          currentPoint = [values[i++], values[i++]]
          currentPath.push([...currentPoint])
        }
        break

      case "t": // Smooth quadratic Bézier curve (relative)
        while (i + 1 < values.length) {
          currentPoint = [
            currentPoint[0] + values[i++],
            currentPoint[1] + values[i++],
          ]
          currentPath.push([...currentPoint])
        }
        break

      case "A": // Absolute
        while (i + 6 < values.length) {
          const rx = values[i++]
          const ry = values[i++]
          const xAxisRotation = values[i++]
          const largeArcFlag = values[i++]
          const sweepFlag = values[i++]
          const x = values[i++]
          const y = values[i++]
          const arcPoints = arcToPoints(
            currentPoint,
            rx,
            ry,
            xAxisRotation,
            largeArcFlag,
            sweepFlag,
            [x, y],
          )
          for (const pt of arcPoints) {
            currentPath.push(pt)
          }
          currentPoint = [x, y]
        }
        break

      case "a": // Relative
        while (i + 6 < values.length) {
          const rx = values[i++]
          const ry = values[i++]
          const xAxisRotation = values[i++]
          const largeArcFlag = values[i++]
          const sweepFlag = values[i++]
          const x = currentPoint[0] + values[i++]
          const y = currentPoint[1] + values[i++]
          const arcPoints = arcToPoints(
            currentPoint,
            rx,
            ry,
            xAxisRotation,
            largeArcFlag,
            sweepFlag,
            [x, y],
          )
          for (const pt of arcPoints) {
            currentPath.push(pt)
          }
          currentPoint = [x, y]
        }
        break

      case "Z":
      case "z": // Close path
        if (currentPath.length) {
          currentPath.push([...currentPath[0]]) // Close the path
          paths.push(currentPath)
          currentPath = []
        }
        break

      default:
        console.warn(`Unsupported command: ${type}`)
    }
  }

  if (currentPath.length) {
    paths.push(currentPath)
  }

  return paths
}

export class MarkerDrawing extends StateNode {
  static override id = "marker-drawing"

  page = this.editor.getCurrentPage() as LDPage

  info = {} as TLPointerEventInfo

  initialShape?: MarkerShape

  override shapeType = "marker-shape"

  util = this.editor.getShapeUtil(this.shapeType)

  isPen = false
  isPenOrStylus = false

  segmentMode = "free" as
    | "free"
    | "straight"
    | "starting_straight"
    | "starting_free"

  didJustShiftClickToExtendPreviousShapeLine = false

  pagePointWhereCurrentSegmentChanged = {} as Vec

  pagePointWhereNextSegmentChanged = null as Vec | null

  lastRecordedPoint = {} as Vec
  mergeNextPoint = false
  currentLineLength = 0

  markId = null as null | string

  override onEnter(info: TLPointerEventInfo) {
    this.markId = null
    this.info = info
    this.lastRecordedPoint = this.editor.inputs.currentPagePoint.clone()
    this.startShape()
  }

  override onPointerMove() {
    const { inputs } = this.editor

    if (this.isPen && !inputs.isPen) {
      // The user made a palm gesture before starting a pen gesture;
      // ideally we'd start the new shape here but we could also just bail
      // as the next interaction will work correctly
      if (this.markId) {
        this.editor.bailToMark(this.markId)
        this.startShape()
        return
      }
    }

    if (this.isPenOrStylus) {
      // Don't update the shape if we haven't moved far enough from the last time we recorded a point
      if (
        Vec.Dist(inputs.currentPagePoint, this.lastRecordedPoint) >=
        0.2 / this.editor.getZoomLevel()
      ) {
        this.lastRecordedPoint = inputs.currentPagePoint.clone()
        this.mergeNextPoint = false
      } else {
        this.mergeNextPoint = true
      }
    } else {
      this.mergeNextPoint = false
    }

    this.updateDrawingShape()
  }

  override onExit() {
    this.editor.snaps.clearIndicators()
    this.pagePointWhereCurrentSegmentChanged =
      this.editor.inputs.currentPagePoint.clone()
  }

  // canClose() {
  // 	// alvaro: modified
  // 	return this.shapeType !== 'custom-highlight'
  // }

  // alvaro: removed tldraw "size" and added "strokeWidth"
  // getIsClosed(segments: TLDrawShapeSegment[], strokeWidth: number, scale: number) {
  // 	if (!this.canClose()) return false

  // 	// alvaro: remove STROKE_SIZES code
  // 	const firstPoint = segments[0].points[0]
  // 	const lastSegment = segments[segments.length - 1]
  // 	const lastPoint = lastSegment.points[lastSegment.points.length - 1]

  // 	return (
  // 		firstPoint !== lastPoint &&
  // 		this.currentLineLength > strokeWidth * 4 * scale &&
  // 		Vec.DistMin(firstPoint, lastPoint, strokeWidth * 2 * scale)
  // 	)
  // }

  private startShape() {
    const {
      inputs: { originPagePoint, isPen },
    } = this.editor

    this.markId = this.editor.markHistoryStoppingPoint("draw start")

    // If the pressure is weird, then it's probably a stylus reporting as a mouse
    // We treat pen/stylus inputs differently in the drawing tool, so we need to
    // have our own value for this. The inputs.isPen is only if the input is a regular
    // pen, like an iPad pen, which needs to trigger "pen mode" in order to avoid
    // accidental palm touches. We don't have to worry about that with styluses though.
    const { z = 0.5 } = this.info.point

    this.isPen = isPen
    this.isPenOrStylus = isPen || (z > 0 && z < 0.5) || (z > 0.5 && z < 1)

    const pressure = this.isPenOrStylus ? z * 1.25 : 0.5

    this.segmentMode = this.editor.inputs.shiftKey ? "straight" : "free"

    this.didJustShiftClickToExtendPreviousShapeLine = false

    this.lastRecordedPoint = originPagePoint.clone()

    if (this.initialShape) {
      const shape = this.editor.getShape<MarkerShape>(this.initialShape.id)

      if (shape && this.segmentMode === "straight") {
        // Connect dots

        this.didJustShiftClickToExtendPreviousShapeLine = true

        const prevSegment = last(shape.props.segments)
        if (!prevSegment) throw Error("Expected a previous segment!")
        const prevPoint = last(prevSegment.points)
        if (!prevPoint) throw Error("Expected a previous point!")

        const { x, y } = this.editor
          .getPointInShapeSpace(shape, originPagePoint)
          .toFixed()

        const newSegment: TLDrawShapeSegment = {
          type: this.segmentMode,
          points: [
            {
              x: prevPoint.x,
              y: prevPoint.y,
              z: +pressure.toFixed(2),
            },
            {
              x,
              y,
              z: +pressure.toFixed(2),
            },
          ],
        }

        // Convert prevPoint to page space
        const prevPointPageSpace = Mat.applyToPoint(
          this.editor.getShapePageTransform(shape.id)!,
          prevPoint,
        )
        this.pagePointWhereCurrentSegmentChanged = prevPointPageSpace
        this.pagePointWhereNextSegmentChanged = null
        const segments = [...shape.props.segments, newSegment]

        // alvaro: hard-coded "size" reference to just 'm'
        if (this.currentLineLength < STROKE_SIZES["m"] * 4) {
          this.currentLineLength = this.getLineLength(segments)
        }

        const instanceMeta = this.editor.getInstanceState()
          .meta as LDInstanceMeta

        console.log("INSTANCE META", instanceMeta)
        const shapePartial: TLShapePartial<MarkerShape> = {
          id: shape.id,
          type: "marker-shape",
          props: {
            radius: instanceMeta.markerRadius,
            segments,
          },
        }

        // if (this.canClose()) {
        // 	// alvaro: added call to get the strokeWidth from the layer
        // 	const page = this.editor.getCurrentPage().meta as PageMeta
        // 	const strokeWidth = page.layers[page.currentLayer].strokeWidth
        // 		; (shapePartial as TLShapePartial<CustomDrawShape>).props!.isClosed = this.getIsClosed(
        // 			segments,
        // 			strokeWidth,
        // 			shape.props.scale
        // 		)
        // }

        this.editor.updateShapes<MarkerShape>([shapePartial])

        return
      }
    }

    // Create a new shape

    this.pagePointWhereCurrentSegmentChanged = originPagePoint.clone()
    const id = createShapeId()

    // alvaro: reference to the page
    const pageMeta = (this.editor.getCurrentPage() as LDPage).meta
    // const color = pageMeta.currentColor
    // console.log("color", color)

    const instanceMeta = this.editor.getInstanceState().meta as LDInstanceMeta

    console.log("INSTANCE META2", instanceMeta)

    const color = instanceMeta.fillColor

    this.editor.createShape<CustomDrawShape>({
      id: createShapeId(),
      type: "custom-draw",
      x: originPagePoint.x,
      y: originPagePoint.y,
      index: pageMeta.layers[pageMeta.currentLayer].index,
      props: {
        // baseOrientation: "horizontal",
        isComplete: false,
        isPen: this.isPenOrStylus,
        isFill: true,

        layer: pageMeta.currentLayer,
        segments: [
          {
            type: this.segmentMode,
            points: [
              {
                x: 0,
                y: 0,
                z: +pressure.toFixed(2),
              },
            ],
          },
        ],
        parameterValues: {
          visible: Expr.lit(true),
          fillColor: Expr.lit(color),
          //roundness: Expr.lit(0),
          opacity: Expr.lit(1),
        },
      },
    })

    this.editor.createShapes<MarkerShape>([
      {
        id,
        type: "marker-shape",
        x: originPagePoint.x,
        y: originPagePoint.y,
        // alvaro: get index from the page
        index: pageMeta.layers[pageMeta.currentLayer].index,
        opacity: 0,
        props: {
          // color,
          color: Expr.lit(color),
          radius: instanceMeta.markerRadius,
          // alvaro: get layer from the page
          layer: pageMeta.currentLayer,
          isPen: this.isPenOrStylus,
          scale: this.editor.user.getIsDynamicResizeMode()
            ? 1 / this.editor.getZoomLevel()
            : 1,
          segments: [
            {
              type: this.segmentMode,
              points: [
                {
                  x: 0,
                  y: 0,
                  z: +pressure.toFixed(2),
                },
              ],
            },
          ],
        },
      },
    ])

    this.currentLineLength = 0
    this.initialShape = this.editor.getShape<MarkerShape>(id)
  }

  private updateDrawingShape() {
    const { initialShape } = this
    const { inputs } = this.editor

    if (!initialShape) return

    const {
      id,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      props: { /*size,*/ scale },
    } = initialShape

    const shape = this.editor.getShape<MarkerShape>(id)!

    if (!shape) return

    const instanceMeta = this.editor.getInstanceState().meta as LDInstanceMeta

    const { segments } = shape.props

    const { x, y } = this.editor
      .getPointInShapeSpace(shape, inputs.currentPagePoint)
      .toFixed()
    const pressure = this.isPenOrStylus
      ? +(inputs.currentPagePoint.z! * 1.25).toFixed(2)
      : 0.5
    const newPoint = { x, y, z: pressure }

    //console.log('free')
    const newSegments = segments.slice()
    const newSegment = newSegments[newSegments.length - 1]
    const newPoints = [...newSegment.points]

    if (newPoints.length && this.mergeNextPoint) {
      const { z } = newPoints[newPoints.length - 1]
      newPoints[newPoints.length - 1] = {
        x: newPoint.x,
        y: newPoint.y,
        z: z ? Math.max(z, newPoint.z) : newPoint.z,
      }
      // Note: we could recompute the line length here, but it's not really necessary
      // this.currentLineLength = this.getLineLength(newSegments)
    } else {
      this.currentLineLength += Vec.Dist(
        newPoints[newPoints.length - 1],
        newPoint,
      )
      newPoints.push(newPoint)
    }

    newSegments[newSegments.length - 1] = {
      ...newSegment,
      points: newPoints,
    }

    // alvaro: hard coded "size" to 'm'
    if (this.currentLineLength < STROKE_SIZES["m"] * 4) {
      this.currentLineLength = this.getLineLength(newSegments)
    }

    const shapePartial: TLShapePartial<MarkerShape> = {
      id,
      type: "marker-shape",
      props: {
        radius: instanceMeta.markerRadius,
        segments: newSegments,
      },
    }

    this.editor.updateShapes([shapePartial])

    const page = this.editor.getCurrentPage() as LDPage

    const allPointsFromSegments = getPointsFromSegments(shape.props.segments)

    const options: StrokeOptions = {
      thinning: 0,
      streamline: 0,
      smoothing: 0,
      size: shape.props.radius * shape.props.scale * 3,
    }

    const paths = parseSVGPath1(svgInk(allPointsFromSegments, options)).map(
      (b) =>
        b.map((a) => {
          return { x: a[0], y: a[1] }
        }),
    )
    const color = Expr.lit("red") // page.meta.currentColor

    paths.forEach((points, i) =>
      this.editor.createShape<CustomDrawShape>({
        id: (shape.id + `${i}`) as TLShapeId,
        type: "custom-draw",
        x: shape.x,
        y: shape.y,
        index: page.meta.layers[page.meta.currentLayer].index,
        props: {
          isPen: shape.props.isPen,
          isFill: true,
          scale: shape.props.scale,

          layer: page.meta.currentLayer,
          isComplete: true,
          segments: [
            {
              type: "free",
              points: points,
            },
          ],
          parameterValues: {
            visible: Expr.lit(true),
            fillColor: shape.props.color,
            opacity: Expr.lit(1),
          },
        },
      }),
    )

    // Set a maximum length for the lines array; after 200 points, complete the line.

    // adam: commented out this code because it was causing the line to complete after 200 points messing up the fill

    // if (newPoints.length > this.editor.options.maxPointsPerDrawShape) {
    // 	this.editor.updateShapes([{ id, type: this.shapeType, props: { isComplete: true } }])

    // 	const newShapeId = createShapeId()

    // 	const props = this.editor.getShape<DrawableShape>(id)!.props

    // 	// alvaro: get index from the page/layer
    // 	const page = this.editor.getCurrentPage().meta as PageMeta
    // 	const layer = page.layers[page.currentLayer]

    // 	this.editor.createShapes<DrawableShape>([
    // 		{
    // 			id: newShapeId,
    // 			type: this.shapeType,
    // 			x: toFixed(inputs.currentPagePoint.x),
    // 			y: toFixed(inputs.currentPagePoint.y),
    // 			index: layer.index,
    // 			props: {
    // 				layer: page.currentLayer,
    // 				isPen: this.isPenOrStylus,
    // 				scale: props.scale,
    // 				segments: [
    // 					{
    // 						type: 'free',
    // 						points: [{ x: 0, y: 0, z: this.isPenOrStylus ? +(z! * 1.25).toFixed() : 0.5 }],
    // 					},
    // 				],
    // 			},
    // 		},
    // 	])

    // 	this.initialShape = structuredClone(this.editor.getShape<DrawableShape>(newShapeId)!)
    // 	this.mergeNextPoint = false
    // 	this.lastRecordedPoint = inputs.currentPagePoint.clone()
    // 	this.currentLineLength = 0
    // }

    // break
  }

  private getLineLength(segments: TLDrawShapeSegment[]) {
    let length = 0

    for (const segment of segments) {
      for (let i = 0; i < segment.points.length - 1; i++) {
        const A = segment.points[i]
        const B = segment.points[i + 1]
        length += Vec.Dist2(B, A)
      }
    }

    return Math.sqrt(length)
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  override onRightClick(info: TLPointerEventInfo): void {
    //console.log('right click')
  }

  override onPointerUp() {
    //console.log('done')
    this.complete()
  }

  override onCancel() {
    this.cancel()
  }

  override onComplete() {
    this.complete()
  }

  override onInterrupt() {
    if (this.editor.inputs.isDragging) {
      return
    }

    if (this.markId) {
      this.editor.bailToMark(this.markId)
    }
    this.cancel()
  }

  complete() {
    const { initialShape } = this

    const page = this.editor.getCurrentPage() as LDPage
    if (!initialShape) return
    this.editor.updateShapes([
      {
        id: initialShape.id,
        type: initialShape.type,
        props: { isComplete: true },
      },
    ])
    const showAsComplete = true
    const shape = this.editor.getShape<MarkerShape>(initialShape.id)!
    const allPointsFromSegments = getPointsFromSegments(shape.props.segments)
    console.log("seg", shape.props.segments)
    const proxyShape = toProxyShape(shape)
    const options: StrokeOptions = {
      thinning: 0,
      streamline: 0,
      smoothing: 0,
      size: shape.props.radius * shape.props.scale * 3,
    }
    console.log("allPointsFromSegments", allPointsFromSegments)

    const paths = parseSVGPath1(svgInk(allPointsFromSegments, options)).map(
      (b) =>
        b.map((a) => {
          return { x: a[0], y: a[1] }
        }),
    )

    console.log("paths", paths)

    const simplifiedPaths = paths
      .map((path) => simplifyPolygon(path.map(({ x, y }) => [x, y] as Point)))
      .filter((path) => path.length > 0)
    console.log("simplifiedPaths", simplifiedPaths)
    const unionedPaths = unionFillShapes(simplifiedPaths)
    console.log("unionedPaths", unionedPaths)
    const segments = unionedPaths.map((path) => {
      console.log("path", path)
      const segment: TLDrawShapeSegment = {
        type: "free" as const,
        points: path.map((point) => ({
          x: point[0],
          y: point[1],
          z: shape.props.isPen ? 1 : 0.5,
        })),
      }
      console.log("segment", segment)

      return segment
    })
    console.log("segments", segments)
    const instanceMeta = this.editor.getInstanceState().meta as LDInstanceMeta
    const color = Expr.lit(instanceMeta.fillColor)

    ;(this.editor.createShape<CustomDrawShape>({
      id: createShapeId(),
      type: "custom-draw",
      x: shape.x,
      y: shape.y,
      index: page.meta.layers[page.meta.currentLayer].index,
      props: {
        isFill: true,
        isPen: shape.props.isPen,
        scale: shape.props.scale,
        layer: page.meta.currentLayer,
        isComplete: true,
        segments: segments as TLDrawShapeSegment[],
        parameterValues: {
          fillColor: color,
          visible: Expr.lit(true),
          opacity: Expr.lit(1),
        },
      },
    }),
      this.editor.deleteShape(initialShape.id))
    this.editor.deleteShapes(
      paths.map((_, i) => initialShape.id + `${i}`) as TLShapeId[],
    )

    this.parent.transition("marker-idle")
  }

  cancel() {
    this.parent.transition("marker-idle", this.info)
  }
}
